import React, { useState, useEffect, useCallback, useRef } from "react";
import { attendanceService } from "../../services/attendanceService";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import CreateEventModal from "./CreateEventModal";
import "../Teams/TeamsPage.css";
import "./EventDetailModal.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDateTime = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const StatusBadge = ({ status }) => {
  const map = {
    active: { label: "Active", cls: "active" },
    completed: { label: "Completed", cls: "completed" },
    cancelled: { label: "Cancelled", cls: "cancelled" },
    draft: { label: "Draft", cls: "draft" },
  };
  const s = map[status] || { label: status, cls: "draft" };
  return <span className={`status-badge ${s.cls}`}>{s.label}</span>;
};

const MethodBadge = ({ method }) => {
  const label = method === "qr_scan" || method === "qr" ? "QR Scan" : "Manual";
  const cls = method === "qr_scan" || method === "qr" ? "qr_scan" : "manual";
  return <span className={`method-badge ${cls}`}>{label}</span>;
};

// ─── DeleteEventModal ─────────────────────────────────────────────────────────

const DeleteEventModal = ({ event, onClose, onDeleted }) => {
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await attendanceService.deleteAttendanceEvent(
        event.id,
        user?.jwt
      );
      if (res.success) {
        onDeleted();
        onClose();
      }
    } catch {
      /* ignore */
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Delete Event</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="delete-confirm-body">
            <span className="material-icons">warning</span>
            <p>
              Are you sure you want to delete <strong>{event.title}</strong>?
              All attendance records will be lost. This cannot be undone.
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete Event"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── EventDetailModal ─────────────────────────────────────────────────────────

const EventDetailModal = ({
  event: initialEvent,
  onClose,
  onUpdated,
  onDeleted,
}) => {
  const { user } = useAuth();

  const [event, setEvent] = useState(initialEvent);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [attendees, setAttendees] = useState([]);
  const [attendeesLoading, setAttendeesLoading] = useState(true);

  // Manual mark state — name search
  const [repSearch, setRepSearch] = useState("");
  const [selectedRep, setSelectedRep] = useState(null); // { id, name }
  const [repList, setRepList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState("");
  const [markSuccess, setMarkSuccess] = useState("");

  // Sub-modal state
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  // Auto-refresh interval ref
  const refreshIntervalRef = useRef(null);

  // ─── Fetch QR code ──────────────────────────────────────────────────────────
  const loadQrCode = useCallback(async () => {
    setQrLoading(true);
    try {
      const res = await attendanceService.getEventQrCode(event.id, user?.jwt);
      if (res.success && res.data?.qrCode) {
        setQrCode(res.data.qrCode);
      } else if (res.qrCode) {
        setQrCode(res.qrCode);
      }
    } catch {
      /* ignore */
    } finally {
      setQrLoading(false);
    }
  }, [event.id, user?.jwt]);

  // ─── Fetch attendees ────────────────────────────────────────────────────────
  const loadAttendees = useCallback(async () => {
    try {
      const res = await attendanceService.getEventAttendees(
        event.id,
        user?.jwt
      );
      if (res.success && Array.isArray(res.data)) {
        setAttendees(res.data);
      } else if (Array.isArray(res)) {
        setAttendees(res);
      }
    } catch {
      /* ignore */
    } finally {
      setAttendeesLoading(false);
    }
  }, [event.id, user?.jwt]);

  // ─── Fetch company reps for name search ─────────────────────────────────────
  useEffect(() => {
    const fetchReps = async () => {
      try {
        const res = await fetch(getApiEndpoint("/v1/company/reps"), {
          headers: { Authorization: `Bearer ${user?.jwt}` },
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.reps || [];
          setRepList(
            list.map(r => ({
              id: r.user_season_id || r.id,
              userId: r.user_id || r.id,
              name:
                `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() ||
                r.email ||
                `User #${r.id}`,
            }))
          );
        }
      } catch {
        /* ignore */
      }
    };
    fetchReps();
  }, [user?.jwt]);

  // Initial load
  useEffect(() => {
    loadQrCode();
    loadAttendees();
  }, [loadQrCode, loadAttendees]);

  // Auto-refresh attendees every 30 seconds
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      loadAttendees();
    }, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadAttendees]);

  // ─── Download QR code ───────────────────────────────────────────────────────
  const handleDownloadQr = () => {
    if (!qrCode) return;
    const link = document.createElement("a");
    link.href = qrCode;
    link.download = `attendance-qr-${event.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Print QR code ──────────────────────────────────────────────────────────
  const handlePrintQr = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code – ${event.title}</title>
          <style>
            body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; }
            h2 { font-size: 20px; margin-bottom: 8px; }
            p { font-size: 14px; color: #666; margin: 0 0 16px; }
            img { width: 300px; height: 300px; }
          </style>
        </head>
        <body>
          <h2>${event.title}</h2>
          <p>${fmtDateTime(event.scheduled_time)}</p>
          ${qrCode ? `<img src="${qrCode}" alt="QR Code" />` : "<p>QR code not available</p>"}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  // ─── Manual mark attendance ─────────────────────────────────────────────────
  const handleManualMark = async () => {
    if (!selectedRep) return;
    setMarking(true);
    setMarkError("");
    setMarkSuccess("");
    try {
      const res = await attendanceService.manualMarkAttendance(
        event.id,
        selectedRep.userId,
        user?.jwt
      );
      if (res.success) {
        setMarkSuccess(`Attendance marked for ${selectedRep.name}.`);
        setSelectedRep(null);
        setRepSearch("");
        loadAttendees();
      } else {
        setMarkError(res.message || "Failed to mark attendance.");
      }
    } catch (err) {
      setMarkError(err.message || "Something went wrong.");
    } finally {
      setMarking(false);
    }
  };

  // ─── Filtered rep list ───────────────────────────────────────────────────────
  const filteredReps =
    repSearch.trim().length > 0
      ? repList
          .filter(r => r.name.toLowerCase().includes(repSearch.toLowerCase()))
          .slice(0, 8)
      : [];

  // ─── Handle edit saved ──────────────────────────────────────────────────────
  const handleEditSaved = updatedEvent => {
    setEvent(updatedEvent);
    if (onUpdated) onUpdated(updatedEvent);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div
          className="modal-container event-detail"
          onClick={e => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="modal-header">
            <h2>{event.title}</h2>
            <button className="modal-close" onClick={onClose}>
              <span className="material-icons">close</span>
            </button>
          </div>

          <div className="modal-body">
            {/* ── Event Info Grid ── */}
            <div className="event-info-grid">
              <div className="event-info-item">
                <span className="event-info-label">Date & Time</span>
                <span className="event-info-value">
                  {fmtDateTime(event.scheduled_time)}
                </span>
              </div>
              {event.location && (
                <div className="event-info-item">
                  <span className="event-info-label">Location</span>
                  <span className="event-info-value">{event.location}</span>
                </div>
              )}
              <div className="event-info-item">
                <span className="event-info-label">Status</span>
                <span className="event-info-value">
                  <StatusBadge status={event.status} />
                </span>
              </div>
              {event.duration_minutes && (
                <div className="event-info-item">
                  <span className="event-info-label">Duration</span>
                  <span className="event-info-value">
                    {event.duration_minutes} min
                  </span>
                </div>
              )}
              {event.description && (
                <div
                  className="event-info-item"
                  style={{ gridColumn: "1 / -1" }}
                >
                  <span className="event-info-label">Description</span>
                  <span className="event-info-value">{event.description}</span>
                </div>
              )}
            </div>

            {/* ── QR Code Section ── */}
            <div className="qr-code-section">
              <div className="qr-code-section-title">
                <span
                  className="material-icons"
                  style={{
                    fontSize: 14,
                    verticalAlign: "middle",
                    marginRight: 4,
                  }}
                >
                  qr_code_2
                </span>
                Attendance QR Code
              </div>

              <div className="qr-code-image-wrapper">
                {qrLoading ? (
                  <div className="qr-code-placeholder">
                    <span className="material-icons">sync</span>
                    <span>Loading QR code…</span>
                  </div>
                ) : qrCode ? (
                  <img
                    src={qrCode}
                    alt="Attendance QR Code"
                    className="qr-code-image"
                  />
                ) : (
                  <div className="qr-code-placeholder">
                    <span className="material-icons">qr_code</span>
                    <span>QR code unavailable</span>
                  </div>
                )}
              </div>

              <div className="qr-actions">
                <button
                  className="btn-qr-action download"
                  onClick={handleDownloadQr}
                  disabled={!qrCode}
                >
                  <span className="material-icons">download</span>
                  Download QR Code
                </button>
                <button
                  className="btn-qr-action print"
                  onClick={handlePrintQr}
                  disabled={!qrCode}
                >
                  <span className="material-icons">print</span>
                  Print QR Code
                </button>
              </div>
            </div>

            {/* ── Attendees Section ── */}
            <div className="attendees-section">
              <div className="attendees-section-header">
                <span className="attendees-section-title">Attendees</span>
                <span className="attendees-count-badge">
                  {attendees.length}{" "}
                  {attendees.length === 1 ? "attendee" : "attendees"}
                </span>
              </div>

              <div className="attendees-table-wrapper">
                {attendeesLoading ? (
                  <div className="attendees-empty">Loading attendees…</div>
                ) : attendees.length === 0 ? (
                  <div className="attendees-empty">
                    No attendees yet. Share the QR code to start tracking
                    attendance.
                  </div>
                ) : (
                  <table className="attendees-table">
                    <thead>
                      <tr>
                        <th>Rep Name</th>
                        <th>Check-in Time</th>
                        <th>Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendees.map((a, i) => (
                        <tr key={a.id || i}>
                          <td className="attendee-name">
                            {a.user?.first_name || a.user?.last_name
                              ? `${a.user.first_name ?? ""} ${a.user.last_name ?? ""}`.trim()
                              : a.first_name || a.name
                                ? `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() ||
                                  a.name
                                : `User #${a.user_id}`}
                          </td>
                          <td>
                            {fmtDateTime(a.checked_in_at || a.created_at)}
                          </td>
                          <td>
                            <MethodBadge
                              method={a.check_in_method || a.method || "manual"}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <p className="auto-refresh-note">
                Auto-refreshes every 30 seconds
              </p>
            </div>

            {/* ── Manual Mark Section ── */}
            <div className="manual-mark-section">
              <div className="manual-mark-title">
                <span
                  className="material-icons"
                  style={{
                    fontSize: 14,
                    verticalAlign: "middle",
                    marginRight: 4,
                  }}
                >
                  how_to_reg
                </span>
                Mark Attendance Manually
              </div>
              <div className="manual-mark-row" style={{ position: "relative" }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <input
                    type="text"
                    value={selectedRep ? selectedRep.name : repSearch}
                    onChange={e => {
                      setRepSearch(e.target.value);
                      setSelectedRep(null);
                      setMarkError("");
                      setMarkSuccess("");
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    placeholder="Search by name…"
                    style={{ width: "100%" }}
                  />
                  {showDropdown && filteredReps.length > 0 && (
                    <ul className="rep-search-dropdown">
                      {filteredReps.map(r => (
                        <li
                          key={r.id}
                          onMouseDown={() => {
                            setSelectedRep(r);
                            setRepSearch("");
                            setShowDropdown(false);
                            setMarkError("");
                            setMarkSuccess("");
                          }}
                        >
                          {r.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  className="btn-mark"
                  onClick={handleManualMark}
                  disabled={!selectedRep || marking}
                >
                  {marking ? "Marking…" : "Mark"}
                </button>
              </div>
              {markError && <p className="manual-mark-error">{markError}</p>}
              {markSuccess && (
                <p className="manual-mark-success">{markSuccess}</p>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="modal-footer">
            <div className="event-detail-footer-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEdit(true)}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <span className="material-icons" style={{ fontSize: 16 }}>
                  edit
                </span>
                Edit
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowDelete(true)}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <span className="material-icons" style={{ fontSize: 16 }}>
                  delete
                </span>
                Delete
              </button>
            </div>
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {showEdit && (
        <CreateEventModal
          event={event}
          onClose={() => setShowEdit(false)}
          onSaved={handleEditSaved}
        />
      )}

      {/* ── Delete Modal ── */}
      {showDelete && (
        <DeleteEventModal
          event={event}
          onClose={() => setShowDelete(false)}
          onDeleted={() => {
            if (onDeleted) onDeleted(event.id);
            onClose();
          }}
        />
      )}
    </>
  );
};

export default EventDetailModal;
